import { useMemo } from 'react';
import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTutorWishlistCaseOptions,
  getTutorWishlists,
  replaceTutorWishlistCases,
} from '@/api/services/wishlistsApi';
import type {
  GetTutorWishlistsParams,
  TutorData,
  TutorWishlistData,
  TutorWishlistMutationData,
} from '@/types/api';
import { useAuthContext } from '@/contexts';
import { logger } from '@/utils/logger';
import {
  getViewerIdentity,
  tutorQueryKeys,
  tutorWishlistMutationKey,
  wishlistTutorQueryKeys,
} from './queryKeys';

interface UseWishlistTutorsParams extends GetTutorWishlistsParams {
  enabled?: boolean;
}

export interface SetTutorWishlistVariables {
  tutorId: string;
  caseIds: string[];
  debugRequestId?: string;
}

type TutorWishlistQueryData = {
  data: TutorWishlistData[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
};

type PendingWishlistMutation = SetTutorWishlistVariables & {
  desired: boolean;
  submittedAt: number;
};

const updateTutorWishlistState = (
  old: { tutors: TutorData[]; pagination: { page: number; limit: number; total: number; totalPages: number } } | undefined,
  tutorId: string,
  isWishlisted: boolean
) => {
  if (!old) return old;
  return {
    ...old,
    tutors: old.tutors.map((tutor) =>
      tutor.id === tutorId ? { ...tutor, isWishlisted } : tutor
    ),
  };
};

const updateTutorDetailWishlistState = (
  old: TutorData | undefined,
  isWishlisted: boolean
) => old ? { ...old, isWishlisted } : old;

const updateWishlistList = (
  old: TutorWishlistQueryData | undefined,
  response: TutorWishlistMutationData
) => {
  if (!old?.data) return old;
  const entryIndex = old.data.findIndex((entry) => entry.tutorId === response.tutorId);

  if (!response.isWishlisted) {
    if (entryIndex < 0) return old;
    const data = old.data.filter((entry) => entry.tutorId !== response.tutorId);
    const total = Math.max(0, old.pagination.total - 1);
    return {
      ...old,
      data,
      pagination: {
        ...old.pagination,
        total,
        totalPages: Math.ceil(total / old.pagination.limit),
      },
    };
  }

  if (entryIndex < 0) return old;
  return {
    ...old,
    data: old.data.map((entry) =>
      entry.tutorId === response.tutorId
        ? {
            ...entry,
            linkedCases: response.linkedCases,
            linkedCaseCount: response.linkedCases.length,
            isWishlisted: true,
            tutorAvailable: response.tutorAvailable,
          }
        : entry
    ),
  };
};

export const getOptimisticWishlistState = (
  tutorId: string,
  serverIsWishlisted: boolean | undefined,
  overlayMap: Map<string, boolean>
) => overlayMap.get(tutorId) ?? !!serverIsWishlisted;

export const useTutorWishlistOverlayMap = () => {
  const { firebaseUser, profile } = useAuthContext();
  const viewerIdentity = getViewerIdentity(firebaseUser, profile);
  const pendingMutations = useMutationState<PendingWishlistMutation | null>({
    filters: { mutationKey: tutorWishlistMutationKey(viewerIdentity), status: 'pending' },
    select: (mutation) => {
      const variables = mutation.state.variables as SetTutorWishlistVariables | undefined;
      return variables
        ? {
            ...variables,
            desired: variables.caseIds.length > 0,
            submittedAt: mutation.state.submittedAt,
          }
        : null;
    },
  });

  return useMemo(() => {
    const overlayMap = new Map<string, boolean>();
    pendingMutations
      .filter((item): item is PendingWishlistMutation => item !== null)
      .sort((a, b) => a.submittedAt - b.submittedAt)
      .forEach((item) => overlayMap.set(item.tutorId, item.desired));
    return overlayMap;
  }, [pendingMutations]);
};

export const useTutorWishlistCaseOptions = (
  tutorId: string | undefined,
  enabled: boolean
) => {
  const { firebaseUser, profile } = useAuthContext();
  const viewerIdentity = getViewerIdentity(firebaseUser, profile);

  return useQuery({
    queryKey: wishlistTutorQueryKeys.caseOptions(viewerIdentity, tutorId),
    queryFn: ({ signal }) => getTutorWishlistCaseOptions(tutorId!, signal),
    enabled: enabled && !!tutorId,
    staleTime: 30_000,
  });
};

export const useWishlistTutors = (params?: UseWishlistTutorsParams) => {
  const { enabled = true, page, limit, includeProfile, ...queryParams } = params || {};
  const { firebaseUser, profile } = useAuthContext();
  const viewerIdentity = getViewerIdentity(firebaseUser, profile);

  return useQuery({
    queryKey: wishlistTutorQueryKeys.list(viewerIdentity, {
      page,
      limit,
      includeProfile,
      ...queryParams,
    }),
    queryFn: ({ signal }) =>
      getTutorWishlists({ page, limit, includeProfile, ...queryParams }, signal),
    enabled,
    retry: 1,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
};

export const useSetTutorWishlistMutation = () => {
  const queryClient = useQueryClient();
  const { firebaseUser, profile } = useAuthContext();
  const viewerIdentity = getViewerIdentity(firebaseUser, profile);

  return useMutation({
    mutationKey: tutorWishlistMutationKey(viewerIdentity),
    mutationFn: ({ tutorId, caseIds, debugRequestId }: SetTutorWishlistVariables) =>
      replaceTutorWishlistCases(tutorId, { caseIds }, debugRequestId),
    onSuccess: (response, variables) => {
      queryClient.setQueriesData(
        { queryKey: tutorQueryKeys.all(viewerIdentity) },
        (old: { tutors: TutorData[]; pagination: { page: number; limit: number; total: number; totalPages: number } } | undefined) =>
          updateTutorWishlistState(old, variables.tutorId, response.isWishlisted)
      );
      queryClient.setQueryData(
        tutorQueryKeys.detail(viewerIdentity, variables.tutorId),
        (old: TutorData | undefined) => updateTutorDetailWishlistState(old, response.isWishlisted)
      );
      queryClient.setQueriesData(
        { queryKey: wishlistTutorQueryKeys.all(viewerIdentity) },
        (old: TutorWishlistQueryData | undefined) => updateWishlistList(old, response)
      );
      queryClient.invalidateQueries({
        queryKey: wishlistTutorQueryKeys.caseOptions(viewerIdentity, variables.tutorId),
      });
      queryClient.invalidateQueries({ queryKey: wishlistTutorQueryKeys.all(viewerIdentity) });
    },
    onError: (error, variables) => {
      logger.error('useWishlistTutors', `Failed to update cases for ${variables.tutorId}`, error);
    },
  });
};
